import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CART_STORAGE_KEY } from "../data/catalog";

const CartContext = createContext(null);

const load = () => {
    try {
        const raw = localStorage.getItem(CART_STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

const persist = (items) => {
    try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch {}
};

const makeKey = (item) =>
    [item.listing_slug || item.id, item.adultSize || "", item.kidsSize || "", item.variant || ""].join("|");

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(load);
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        persist(items);
    }, [items]);

    const addItem = useCallback((item) => {
        setItems((prev) => {
            const key = makeKey(item);
            const existing = prev.find((p) => makeKey(p) === key);
            if (existing) {
                return prev.map((p) =>
                    makeKey(p) === key ? { ...p, qty: p.qty + (item.qty || 1) } : p
                );
            }
            return [...prev, { ...item, qty: item.qty || 1 }];
        });
        setDrawerOpen(true);
    }, []);

    const removeItem = useCallback((key) => {
        setItems((prev) => prev.filter((p) => makeKey(p) !== key));
    }, []);

    const updateQty = useCallback((key, qty) => {
        if (qty <= 0) {
            setItems((prev) => prev.filter((p) => makeKey(p) !== key));
            return;
        }
        setItems((prev) => prev.map((p) => (makeKey(p) === key ? { ...p, qty } : p)));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const value = useMemo(() => {
        const totalCents = items.reduce((s, p) => s + (p.priceCents || 0) * p.qty, 0);
        const totalCount = items.reduce((s, p) => s + p.qty, 0);
        return {
            items,
            totalCents,
            totalCount,
            drawerOpen,
            setDrawerOpen,
            addItem,
            removeItem,
            updateQty,
            clear,
            makeKey,
        };
    }, [items, drawerOpen, addItem, removeItem, updateQty, clear]);

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
    return ctx;
};

export const fmtPrice = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
